import { useMemo } from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { SentimentGranularity, SentimentTrendBucket } from "@type/aiqm";

interface SentimentTrendChartProps {
    trend: SentimentTrendBucket[];
    granularity: SentimentGranularity;
}

// Parse a bucket_start as a CALENDAR date in local time, not UTC. The backend
// sends day-resolution bucket starts ("2026-05-01" or "2026-05-01T00:00:00Z");
// `new Date(iso)` would treat the date-only / Z form as UTC midnight, so a user
// in a negative UTC offset would see the previous day on the axis — wrong for a
// time-bucketed analytics screen. We pull the Y-M-D out and build a local Date.
function parseBucketDate(iso: string): Date | null {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const fallback = new Date(iso);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// Format a bucket_start ISO date for the X axis, compactly per granularity.
function formatBucketLabel(iso: string, granularity: SentimentGranularity): string {
    const d = parseBucketDate(iso);
    if (!d) return iso;
    if (granularity === "monthly") {
        return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    }
    // daily + weekly: "5 May 2026"
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const POSITIVE = "#10b981"; // emerald-500
const NEGATIVE = "#f43f5e"; // rose-500

function TrendTooltip({
    active,
    payload,
    granularity,
}: {
    active?: boolean;
    payload?: Array<{ payload: SentimentTrendBucket }>;
    granularity: SentimentGranularity;
}) {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload;
    const d = parseBucketDate(point.bucket_start);
    const dateLabel = d
        ? d.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : point.bucket_start;
    const net = point.net_sentiment;
    const netColor = net >= 0 ? "text-emerald-600" : "text-rose-600";
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md">
            <p className="text-xs font-medium text-gray-500">
                {granularity === "weekly" ? "Week of " : ""}
                {dateLabel}
            </p>
            <p className={`text-sm font-semibold ${netColor}`}>
                Net sentiment: {net > 0 ? "+" : ""}
                {net}
            </p>
            <p className="text-xs text-gray-500">
                {point.call_count} call{point.call_count === 1 ? "" : "s"}
            </p>
        </div>
    );
}

export default function SentimentTrendChart({
    trend,
    granularity,
}: SentimentTrendChartProps) {
    // Average net sentiment across the visible range, for the reference line.
    const avgNet = useMemo(() => {
        if (trend.length === 0) return 0;
        const sum = trend.reduce((s, b) => s + b.net_sentiment, 0);
        return Math.round(sum / trend.length);
    }, [trend]);

    return (
        <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={trend}
                    margin={{ top: 10, right: 8, left: 8, bottom: 0 }}
                >
                    <defs>
                        <linearGradient id="sentimentPositive" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={POSITIVE} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={POSITIVE} stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                        dataKey="bucket_start"
                        tickFormatter={(v: string) => formatBucketLabel(v, granularity)}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        minTickGap={16}
                    />
                    <YAxis
                        domain={[-100, 100]}
                        ticks={[-100, -50, 0, 50, 100]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        width={44}
                    />
                    {/* Zero line: above = net positive, below = net negative */}
                    <ReferenceLine y={0} stroke="#cbd5e1" strokeWidth={1} />
                    {/* Range average, dashed */}
                    <ReferenceLine
                        y={avgNet}
                        stroke="#6366f1"
                        strokeDasharray="4 4"
                        strokeOpacity={0.6}
                    />
                    <Tooltip
                        content={<TrendTooltip granularity={granularity} />}
                        cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
                    />
                    <Area
                        type="monotone"
                        dataKey="net_sentiment"
                        stroke={POSITIVE}
                        strokeWidth={2}
                        fill="url(#sentimentPositive)"
                        // A single bucket has no line to draw, so show a resting
                        // dot for that point; multi-point series stay clean (dot
                        // only on hover via activeDot).
                        dot={
                            trend.length <= 1
                                ? { r: 4, fill: POSITIVE, stroke: "#fff", strokeWidth: 2 }
                                : false
                        }
                        activeDot={{ r: 4, fill: POSITIVE, stroke: "#fff", strokeWidth: 2 }}
                    />
                    {/* Subtle negative reference so a fully-negative series still reads */}
                    <ReferenceLine y={-100} stroke={NEGATIVE} strokeOpacity={0} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}
